import type { FC } from "react";

import { useAuth } from "@/providers/auth/hooks";
import { OrgRecord } from "@/shared/domain/orgs";
import { VisibilityIcon } from "@/shared/foundation/icons/mui";
import { Button } from "@/shared/foundation/primitives";

import { SystemUserOnly } from "./SystemUserOnly";

type ViewAsOrgButtonProps = {
  /** Organization to view as. */
  org: OrgRecord;
  /** Optional size for the button. Defaults to "medium". */
  size?: "small" | "medium" | "large";
  /** Optional variant for the button. Defaults to "outlined". */
  variant?: "text" | "outlined" | "contained";
};

/**
 * A button that lets system users switch the active org to `org`. Wrapped in
 * SystemUserOnly, so it only renders for system users.
 *
 * Used by the admin all-orgs table to "view as" an arbitrary org from a list —
 * the one place that switches to an org other than whatever is currently being
 * viewed. In-app browsing uses {@link CheckOutOrgButton}, which infers the org
 * from the viewing context instead of taking it as a prop.
 */
export const ViewAsOrgButton: FC<ViewAsOrgButtonProps> = ({
  org,
  size = "medium",
  variant = "outlined",
}) => {
  const { setCurrentOrganization } = useAuth();

  return (
    <SystemUserOnly>
      <Button
        variant={variant}
        onClick={() => setCurrentOrganization(org)}
        size={size}
        startIcon={<VisibilityIcon fontSize="small" />}
      >
        View As
      </Button>
    </SystemUserOnly>
  );
};
